import axios from "axios";
import { useContext, useEffect, useState } from "react";
import showNotification from "../../../utils/showNotification";
import AuthContext from "../../../contexts/AuthContext";
import { createColumnHelper } from "@tanstack/react-table";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import { isBase64 } from "../../../utils/isBase64";
import moment from "moment";

const URL = `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/community-resources`

export default function useCommunityForvet() {
    const { authToken } = useContext(AuthContext)
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState(null)
    const [displayForm, setDisplayForm] = useState(false);
    const [initialValuesEdit, setInitialValuesEdit] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedResourceId, setSelectedResourceId] = useState(null);
    const [hasUpadte, setHasUpadte] = useState(false);

    const axiosConfig = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + authToken,
        }
    }

    useEffect(() => {
        const getResources = async () => {
            setIsLoading(true)
            try {
                const { data } = await axios.get(URL, axiosConfig)
                setData(data);
            } catch (error) {
                showNotification({
                    color: "red",
                    status: "error",
                    title: "Error al cargar los recursos.",
                    message: `Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError}`,
                    autoClose: 12000
                })
            } finally {
                setIsLoading(false)
            }
        }
        getResources()
    }, [authToken, hasUpadte])

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor('title', { header: 'Título' }),
        columnHelper.accessor('category', { header: 'Categoría' }),
        columnHelper.accessor('type', { header: 'Tipo' }),
        columnHelper.accessor('is_active', {
            header: 'Activo',
            cell: info => info.getValue() ? 'Sí' : 'No'
        }),
    ];

    // UTIL: Adaptado para manejar preview_url e images (array)
    const uploadToS3 = async ({ file, fileName, fileDirectory }) => {
        if (!file || !isBase64(file)) return file; // Si no es base64, asumimos que ya es URL

        const base64Data = file.split(',')[1];
        const mimeType = file.split(';')[0].split(':')[1];
        const byteString = atob(base64Data);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: mimeType });

        const formData = new FormData()
        formData.append('directory', fileDirectory)
        formData.append('name', `${fileName}-${Math.random().toString(36).substring(7)}.${mimeType.split('/')[1]}`)
        formData.append('file', blob)

        const { data } = await axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/uploadToS3`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data', "Authorization": `Bearer ${authToken}` } }
        );

        return data.url;
    }

    const handleSubmit = async (values) => {
        try {
            setIsLoading(true);

            // 1. Procesar Preview Image
            let finalPreviewUrl = values.preview_url;
            if (isBase64(values.preview_url)) {
                finalPreviewUrl = await uploadToS3({
                    file: values.preview_url,
                    fileDirectory: `public/resources/previews`,
                    fileName: `preview-${moment().format('DD-MM-YYYY')}`,
                });
            }

            // 2.Procesar Tutorial (Imagen o Video)
            let finalTutorialUrl = values.tutorial_url;
            if (isBase64(values.tutorial_url)) {
                // Detectamos si es video o imagen para la extensión
                const isVideo = values.tutorial_url.includes("video/");
                finalTutorialUrl = await uploadToS3({
                    file: values.tutorial_url,
                    fileDirectory: `public/resources/tutorials`,
                    fileName: `tutorial-${isVideo ? 'video' : 'img'}-${moment().format('DD-MM-YYYY')}`,
                });
            }

            // 3. Procesar Array de Imágenes (Masonry)
            const finalImages = await Promise.all((values.images || []).map(async (img, index) => {
                if (isBase64(img)) {
                    return await uploadToS3({
                        file: img,
                        fileDirectory: `public/resources/gallery`,
                        fileName: `gallery-${index}-${moment().format('DD-MM-YYYY')}`,
                    });
                }
                return img;
            }));

            // 4. Processar descarga
            let finalDownloadUrl = values.download_url;
            if (isBase64(values.download_url)) {
                // Detectamos si es video o imagen para la extensión
                const isVideo = values.download_url.includes("video/");
                finalDownloadUrl = await uploadToS3({
                    file: values.download_url,
                    fileDirectory: `public/resources/downloadFiles`,
                    fileName: `download-${isVideo ? 'video' : 'doc'}-${moment().format('DD-MM-YYYY')}`,
                });
            }

            const payload = {
                ...values,
                preview_url: finalPreviewUrl,
                images: finalImages,
                download_url: finalDownloadUrl,
                tutorial_url: finalTutorialUrl,
                tags: typeof values.tags === 'string' ? values.tags.split(',').map(t => t.trim()) : values.tags
            };

            if (initialValuesEdit) {
                await axios.put(URL, { item: { _id: values._id }, field: payload }, axiosConfig);
            } else {
                await axios.post(URL, { data: payload }, axiosConfig);
            }


            showNotification({
                color: 'green',
                title: 'Operación exitosa',
                message: `Recurso ${initialValuesEdit ? 'editado' : 'creado'} con éxito.`,
            });

            setHasUpadte(!hasUpadte);
            setDisplayForm(false);
        } catch (error) {
            console.log(error)
            const message_error = error?.response?.data?.message
            showNotification({
                color: "red",
                title: "Error al guardar el recurso",
                message: `Detalle: ${message_error || ERROR_HANDLER[error?.response?.status] || ERROR_HANDLER.defaultError}`,
            });
        } finally {
            setIsLoading(false);
        }
    }

    const editNew = (rowData) => {
        if (rowData) {
            setInitialValuesEdit({ ...rowData });
            setDisplayForm(true);
        }
    }

    const deleteNew = async (rowData) => {
        if (deleteModalOpen) {
            try {
                setIsLoading(true);
                await axios.delete(URL, { ...axiosConfig, data: { _id: selectedResourceId } });
                setHasUpadte(!hasUpadte);
            } catch (error) {
                showNotification({ color: "red", title: "Error al eliminar", message: "No se pudo eliminar el recurso." });
            } finally {
                setIsLoading(false);
                setDeleteModalOpen(false);
            }
        } else {
            setDeleteModalOpen(true);
            setSelectedResourceId(rowData._id);
        }
    }

    const formValidations = {
        title: (val) => !val ? 'El título es requerido' : null,
        category: (val) => !val ? 'La categoría es requerida' : null,
        type: (val) => !val ? 'El tipo de recurso es requerido' : null,
        external_url: (val) => {
            if (val && !/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i.test(val)) return 'URL inválida';
            return null;
        },
    }

    const globalStats = data ? {
        totalViews: data.reduce((acc, r) => acc + (r.stats?.views || 0), 0),
        totalDownloads: data.reduce((acc, r) => acc + (r.stats?.downloads || 0), 0),
        totalExternalClicks: data.reduce((acc, r) => acc + (r.stats?.external_clicks || 0), 0),
        totalTutorialClicks: data.reduce((acc, r) => acc + (r.stats?.tutorial_clicks || 0), 0),
    } : null;

    // Top 5 recursos por total de interacciones
    const topResources = data ? [...data]
        .map(r => ({
            title: r.title,
            category: r.category,
            total: (r.stats?.views || 0) + (r.stats?.downloads || 0) + (r.stats?.external_clicks || 0) + (r.stats?.tutorial_clicks || 0),
            views: r.stats?.views || 0,
            downloads: r.stats?.downloads || 0,
            external_clicks: r.stats?.external_clicks || 0,
            tutorial_clicks: r.stats?.tutorial_clicks || 0,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5)
        : [];

    return {
        formValidations,
        isLoading,
        data,
        columns,
        editNew,
        handleSubmit,
        displayForm,
        setDisplayForm,
        initialValuesEdit,
        setInitialValuesEdit,
        deleteModalOpen,
        setSelectedResourceId,
        setDeleteModalOpen,
        deleteNew,
        globalStats,
        topResources,
    }
}